import type { EninLookupInput } from "@repo/shared/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as eninLookupApi from "@/lib/api/flow-instances/enin-lookup";
import { useInvalidateFlowCompletion } from "./use-flow-completion";

export const eninLookupKeys = {
  // Must match the invalidateKeys emitted by the flow_save_component (enin_lookup) AI tool.
  flowInstanceValue: (id: string | null) =>
    ["flowInstances", id, "enin-lookup", "flow-instance-value"] as const,
};

export function useEninLookupFlowInstanceValue(id: string | null) {
  const { data, error, isLoading } = useQuery({
    queryKey: eninLookupKeys.flowInstanceValue(id),
    queryFn: () => eninLookupApi.getFlowInstanceValue(id!),
    enabled: !!id,
  });
  return {
    inputData: data?.inputData ?? null,
    calculatedData: data?.calculatedData ?? null,
    error,
    isLoading,
  };
}

export function useSearchAndSaveEninLookup(instanceId: string) {
  const queryClient = useQueryClient();
  const invalidateCompletion = useInvalidateFlowCompletion();
  return useMutation({
    mutationFn: (input: EninLookupInput) => eninLookupApi.searchAndSave(instanceId, input),
    onSuccess: (result) => {
      // Completed instances return a bare { success: true } no-op — leave the cache alone.
      if (result.success && result.inputData !== undefined) {
        queryClient.setQueryData(eninLookupKeys.flowInstanceValue(instanceId), {
          inputData: result.inputData,
          calculatedData: result.calculatedData,
        });
        // Slice 10 — completion predicate reads calculatedData.hasBeenChecked from this blob.
        invalidateCompletion(instanceId);
      }
    },
  });
}

/** QP-184 — AML screening of UBOs / roles / representative (batch, one click). */
export function useScreenEninPersons(instanceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => eninLookupApi.screenPersons(instanceId),
    onSuccess: (result) => {
      if (result.success && result.inputData !== undefined) {
        queryClient.setQueryData(eninLookupKeys.flowInstanceValue(instanceId), {
          inputData: result.inputData,
          calculatedData: result.calculatedData,
        });
      }
    },
  });
}

export function useEninCompanySearch(instanceId: string) {
  return useMutation({
    mutationFn: (name: string) => eninLookupApi.searchCompanies(instanceId, name),
  });
}
